import { produce } from 'immer';
import _ from 'lodash';
import { create } from 'zustand';
import { SecurityContextData, useSecurityContextStore } from '../container-security-context';
import { DockerItem, useDockerSettingStore } from '../docker-setting';
import { EnvItem, useEnvironmentVarStore } from '../environment-variable';
import { HealthCheckData, useHealthCheckStore } from '../health-check';
import { useResetWorkload } from '../hooks';
import { ImagePullPolicy, useImageOptionsStore } from '../image-options';
import { LifecycleManageData, useLifecycleManageStore } from '../lifecycle-manage';
import { usePortSettingStore } from '../port-setting';
import { useStartupCmdStore } from '../startup-cmd';
import {
  genSyncTimezoneItem,
  useStorageSettingStore,
  VolumeItem,
  VolumeMountType,
} from '../storage-settings';
import { useSyncHostTimezoneStore } from '../sync-host-timezone';
import { ContainerPort } from '../types';

export interface ContainerVolumeMountItem {
  /** 挂载卷名称 */
  name?: string;
  /** 是否只读 */
  readOnly?: boolean;
  /** 挂载路径 */
  mountPath?: string;
}

/** 添加容器的数据项
 * @description 仅用于用于存储容器的各种配置信息, 状态类型来自于各个子组件
 * @note 备注: 转换 yaml 请在对应的 yamlData 操作中执行
 */
export interface ContainerItem {
  /** 容器设置的状态集 */
  containerSetting: DockerItem;
  /** 端口设置 */
  portSetting: ContainerPort[];
  /** 容器拉取方案 */
  imagePullPolicy?: ImagePullPolicy;
  /** 健康检查 - 存活 */
  livenessProbe?: HealthCheckData;
  /** 健康检查 - 就绪 */
  readinessProbe?: HealthCheckData;
  /** 健康检查 - 启动 */
  startupProbe?: HealthCheckData;
  /** 声明周期管理 */
  lifecycle?: {
    postStart?: LifecycleManageData;
    preStop?: LifecycleManageData;
  };
  /** 启动命令 命令 */
  command?: string;
  /** 启动命令 参数 */
  args?: string;
  /** 是否开启 tty */
  tty?: boolean;
  /** 环境变量 */
  env?: EnvItem[];
  /** 容器安全上下文 */
  securityContext?: SecurityContextData;
  /** 同步主机时区 */
  syncHostTimezone?: boolean;
  /** 挂载卷关联 */
  volumeMounts?: ContainerVolumeMountItem[];
}

interface ContainerState {
  containersArray: ContainerItem[];
  invalidate: boolean;
}

const initialState: ContainerState = {
  containersArray: [],
  invalidate: false,
};

interface ContainerAction {
  /** 添加容器 */
  addContainer: (container: ContainerItem) => void;
  /** 更新容器 */
  updateContainer: (containerName: string, container: ContainerItem) => void;
  /** 更新容器列表: 回显已添加的容器集合 */
  updateContainerArray: (containers: ContainerItem[]) => void;
  /** 删除容器 */
  removeContainer: (containerName: string) => void;
  /** 关联挂载卷和容器的方法 */
  addContainerVolumeMount: (volume: VolumeItem) => void;
  updateContainerVolumeMount: (volume: VolumeItem, oldVolume: VolumeItem) => void;
  /** 移除容器的挂载卷关联 */
  removeContainerVolumeMount: (volume: VolumeItem) => void;
  /** 初始化编辑容器的方法: 点击选中的卡片,弹出 modal 内部的字段初始化 */
  initEditContainer: (containerName: string) => void;
  /** 校验容器数组 */
  validateContainers: () => Promise<boolean>;
  /** 重置状态 */
  reset: () => void;
}

export const useContainerStore = create<ContainerState & ContainerAction>()((set, get) => ({
  ...initialState,
  addContainer: (container) => {
    const containerName = container.containerSetting.containerName;
    /** 如果勾选了同步主机时区 */
    if (container.syncHostTimezone) {
      useStorageSettingStore.getState().addSyncTimeZoneVolume(containerName);
      /** 同步主机时区的容器需要添加挂载卷 */
      const timeZoneVolumeItem = genSyncTimezoneItem(containerName);
      const timeZoneMounts: ContainerVolumeMountItem = {
        name: timeZoneVolumeItem.volumeName,
        readOnly: timeZoneVolumeItem.volumeMounts[0].mountType === VolumeMountType.ReadOnly,
        mountPath: timeZoneVolumeItem.volumeMounts[0].mountPath,
      };
      container.volumeMounts = [timeZoneMounts];
    }
    set((state) => ({
      containersArray: [...(state.containersArray || []), container],
    }));
    /** 副作用 */
    set({ invalidate: false });
  },
  updateContainer: (containerName, container) => {
    /** 副作用: 如果勾选了同步主机时区 */
    if (container.syncHostTimezone) {
      useStorageSettingStore.getState().addSyncTimeZoneVolume(containerName);

      const timeZoneVolumeItem = genSyncTimezoneItem(containerName);
      const timeZoneMounts: ContainerVolumeMountItem = {
        name: timeZoneVolumeItem.volumeName,
        readOnly: timeZoneVolumeItem.volumeMounts[0].mountType === VolumeMountType.ReadOnly,
        mountPath: timeZoneVolumeItem.volumeMounts[0].mountPath,
      };
      /** 更新挂载卷 */
      container.volumeMounts = [container.volumeMounts || [], timeZoneMounts].flat();
    } else {
      useStorageSettingStore.getState().removeSyncTimeZoneVolume(containerName);
      /** 如果没有勾选同步主机时区,则清除挂载卷 */
      container.volumeMounts = container.volumeMounts?.filter(
        (vm) => vm.mountPath !== '/etc/localtime',
      );
    }
    /** 更新状态 */
    set((state) => ({
      containersArray: state.containersArray?.map((item) =>
        item.containerSetting.containerName === containerName ? { ...item, ...container } : item,
      ),
    }));
    /** 副作用: 清洗挂载卷不匹配的容器 */
    useStorageSettingStore.getState().cleanVolumes();
  },
  updateContainerArray: (containers) => {
    set({ containersArray: containers });
    useStorageSettingStore.getState().cleanVolumes();
  },
  removeContainer: (containerName) => {
    set((state) => {
      return {
        containersArray: state.containersArray?.filter(
          (item) => item.containerSetting.containerName !== containerName,
        ),
      };
    });
    /** 副作用: 校验是否添加了容器 */
    set({ invalidate: _.isEmpty(get().containersArray) });
    /** 副作用: 清洗挂载卷不匹配的容器 */
    useStorageSettingStore.getState().cleanVolumes();
  },
  addContainerVolumeMount: (volume) => {
    /** 更新容器的挂载卷
     * - 在 UI 侧只有存储设置的位置可以操作挂载卷的变化,所以这里需要更新容器的挂载卷
     * - 通过 volume 的 volumeMounts 来更新容器的挂载卷
     */
    set((state) => ({
      containersArray: addContainerVolumeMounts(state.containersArray, volume),
    }));
  },
  updateContainerVolumeMount: (volume, oldVolume) => {
    set((state) => ({
      containersArray: updateContainerVolumeMounts(state.containersArray, volume, oldVolume),
    }));
  },
  removeContainerVolumeMount: (volume) => {
    set((state) => ({
      containersArray: removeContainerVolumeMounts(state.containersArray, volume),
    }));
  },
  initEditContainer: (containerName) => {
    /**
     * 根据当前 容器的名字 获取对应的 container 数据,初始化弹框内各个组件的数据
     * 存在选中 checkbox 的组件需要判断是否选中
     */
    const containers = get().containersArray;
    const targetContainer = containers.find(
      (item) => item.containerSetting.containerName === containerName,
    );
    /** 容器设置 */
    useDockerSettingStore.getState().updateDockerSetting(targetContainer?.containerSetting);
    /** 端口设置 */
    usePortSettingStore.getState().updatePorts(targetContainer?.portSetting);
    /** 容器拉取 */
    useImageOptionsStore.getState().updateImagePullPolicy(targetContainer?.imagePullPolicy);
    /** 健康检查 */
    const isHealthcheckSelected =
      !_.isEmpty(targetContainer?.livenessProbe) ||
      !_.isEmpty(targetContainer?.readinessProbe) ||
      !_.isEmpty(targetContainer?.startupProbe);
    useHealthCheckStore.getState().toggleHealthcheckSelected(isHealthcheckSelected);
    useHealthCheckStore.getState().updateAliveCheck(targetContainer?.livenessProbe);
    useHealthCheckStore.getState().updateReadyCheck(targetContainer?.readinessProbe);
    useHealthCheckStore.getState().updateStartupCheck(targetContainer?.startupProbe);
    /** 生命周期管理 */
    const isLifecycleSelected =
      !_.isEmpty(targetContainer?.lifecycle?.postStart) ||
      !_.isEmpty(targetContainer?.lifecycle?.preStop);
    useLifecycleManageStore.getState().toggleLifecycleManageSelected(isLifecycleSelected);
    useLifecycleManageStore.getState().updatePostStartAction(targetContainer?.lifecycle?.postStart);
    useLifecycleManageStore.getState().updatePreStopAction(targetContainer?.lifecycle?.preStop);
    /** 启动命令 */
    const isStartupCmdSelected =
      !_.isEmpty(targetContainer?.command) ||
      !_.isEmpty(targetContainer?.args) ||
      targetContainer?.tty;
    useStartupCmdStore.getState().toggleStartupCmdSelected(isStartupCmdSelected);
    useStartupCmdStore.getState().updateExecutionCommand(targetContainer?.command);
    useStartupCmdStore.getState().updateParameters(targetContainer?.args);
    useStartupCmdStore.getState().updateTTY(targetContainer?.tty);
    /** 环境变量 */
    const isEnvSelected = !!targetContainer?.env;
    useEnvironmentVarStore.getState().toggleEnvironmentVarSelected(isEnvSelected);
    useEnvironmentVarStore.getState().updateEnv(targetContainer?.env);
    /** 容器安全上下文 */
    const isSecurityContextSelected = !!targetContainer?.securityContext;
    useSecurityContextStore.getState().toggleSecurityContextSelected(isSecurityContextSelected);
    useSecurityContextStore.getState().updateSecurityContextData(targetContainer?.securityContext);
    /** 同步主机时区 */
    useSyncHostTimezoneStore
      .getState()
      .toggleSyncHostTimezoneSelected(targetContainer?.syncHostTimezone);
  },
  validateContainers: async () => {
    const containers = get().containersArray;
    const isContainerValid = containers.length > 0;
    set({ invalidate: !isContainerValid });

    return Promise.resolve(isContainerValid);
  },
  reset: () => {
    set({ containersArray: [] });
    useResetWorkload.getState().resetContainerModal();
  },
}));

/** 转换挂载卷到容器 */
function addContainerVolumeMounts(
  containers: ContainerItem[],
  volume: VolumeItem,
): ContainerItem[] {
  // 按 containerName 分组，过滤掉不需要挂载的项
  const validVolumeMounts = _.filter(
    volume.volumeMounts,
    (vm) => vm.mountType !== VolumeMountType.NotMount,
  );
  const mountsGroupedByContainer = _.groupBy(validVolumeMounts, 'containerName');

  // 使用 immer produce 更新 containers
  return produce(containers, (draft) => {
    draft.forEach((container) => {
      const containerName = container.containerSetting.containerName;
      const mounts = mountsGroupedByContainer[containerName];
      if (mounts && mounts.length > 0) {
        // 取最新的挂载配置（后面的项覆盖前面的）
        const latestMount = _.last(mounts);
        // 确保同一挂载卷名称不重复：先过滤掉已有的同名挂载记录
        container.volumeMounts = (container.volumeMounts || []).filter(
          (vm) => vm.name !== volume.volumeName,
        );
        // 添加最新的挂载配置到容器中
        container.volumeMounts.push({
          name: volume.volumeName,
          readOnly: latestMount.mountType === VolumeMountType.ReadOnly,
          mountPath: latestMount.mountPath,
        });
      }
    });
  });
}

/** 移除容器的挂载卷关联 */
function removeContainerVolumeMounts(
  containers: ContainerItem[],
  volume: VolumeItem,
): ContainerItem[] {
  return produce(containers, (draft) => {
    draft.forEach((container) => {
      if (container.volumeMounts) {
        container.volumeMounts = container.volumeMounts.filter(
          (vm) => vm.name !== volume.volumeName,
        );
      }
    });
  });
}

/** 更新挂载卷到容器 */
function updateContainerVolumeMounts(
  containers: ContainerItem[],
  volume: VolumeItem,
  oldVolume: VolumeItem,
): ContainerItem[] {
  // 按 containerName 分组，过滤掉不需要挂载的项
  const validVolumeMounts = _.filter(
    volume.volumeMounts,
    (vm) => vm.mountType !== VolumeMountType.NotMount,
  );
  const mountsGroupedByContainer = _.groupBy(validVolumeMounts, 'containerName');

  // 使用 immer produce 更新 containers
  return produce(containers, (draft) => {
    draft.forEach((container) => {
      const containerName = container.containerSetting.containerName;
      const mounts = mountsGroupedByContainer[containerName];

      // 先移除旧的挂载卷
      if (container.volumeMounts) {
        container.volumeMounts = container.volumeMounts.filter(
          (vm) => vm.name !== oldVolume.volumeName,
        );
      } else {
        container.volumeMounts = [];
      }

      // 如果有新的挂载配置，添加进去
      if (mounts && mounts.length > 0) {
        // 取最新的挂载配置（后面的项覆盖前面的）
        const latestMount = _.last(mounts);
        // 添加最新的挂载配置到容器中
        container.volumeMounts.push({
          name: volume.volumeName,
          readOnly: latestMount.mountType === VolumeMountType.ReadOnly,
          mountPath: latestMount.mountPath,
        });
      }
    });
  });
}
